import { CssObject } from '@/app/appcharge/cssObjectToString';

export type ThumbnailObjectFit = 'cover' | 'contain';

export type ThumbnailRendering =
  /** Paint the thumbnail as the `.card-image` CSS background, as before. */
  | { mode: 'background' }
  /** Render an optimized <img> that reproduces the publisher's background-* choices. */
  | { mode: 'image'; objectFit: ThumbnailObjectFit; objectPosition?: string };

const CARD_IMAGE_SELECTOR = '.card-image';
const BACKGROUND_PATH = { mode: 'background' } as const;

const stripImportant = (value: string): string => value.replace(/\s*!important\s*$/i, '').trim();

/**
 * Publishers style the card thumbnail through custom CSS on `.card-image`.
 * `background-size: cover|contain` and `background-position` translate 1:1 to
 * `object-fit` / `object-position`; anything that swaps or tiles the image
 * (background-image, gradients, repeat, unusual sizes) keeps the CSS path.
 */
export const resolveThumbnailRendering = (customCss?: CssObject): ThumbnailRendering => {
  let objectFit: ThumbnailObjectFit = 'cover';
  let objectPosition: string | undefined;

  for (const [selector, declarations] of Object.entries(customCss ?? {})) {
    if (!selector.includes(CARD_IMAGE_SELECTOR)) continue;

    for (const [property, rawValue] of Object.entries(declarations)) {
      if (typeof rawValue !== 'string') {
        // Nested at-rule (e.g. @media): responsive background changes can't be reproduced.
        if (Object.keys(rawValue).some((nested) => nested.startsWith('background'))) {
          return BACKGROUND_PATH;
        }
        continue;
      }
      if (!property.startsWith('background')) continue;

      const value = stripImportant(rawValue);
      switch (property) {
        case 'background-size':
          if (value !== 'cover' && value !== 'contain') return BACKGROUND_PATH;
          objectFit = value;
          break;
        case 'background-position':
          objectPosition = value;
          break;
        case 'background-repeat':
          if (value !== 'no-repeat') return BACKGROUND_PATH;
          break;
        case 'background-color':
          break;
        case 'background':
          // Shorthand is fine only as a plain color; images, gradients, sizing or
          // repeat keywords would change how the thumbnail is painted.
          if (/url\(|gradient|\/|cover|contain|repeat/i.test(value)) return BACKGROUND_PATH;
          break;
        default:
          // background-image, -blend-mode, -attachment, -clip, -origin, ...
          return BACKGROUND_PATH;
      }
    }
  }

  return { mode: 'image', objectFit, objectPosition };
};
